import React, { useState } from "react";
import { Box, Button, CircularProgress, FormControlLabel, Grid, Switch, TextField, Typography } from "@mui/material";
import WorkIcon from "@mui/icons-material/Work";
import StorefrontIcon from "@mui/icons-material/Storefront";
import BusinessIcon from "@mui/icons-material/Business";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { PolicyEmploymentStatus } from "../../../../interfaces/DTO/mislaka_xml_pension_models";
import { EmploymentTypeChip } from "../../../PersonFinancialData/Chips/EmploymentTypeChip";
import CurrencyTextField from "../../../../shared/UIParts/InputFields/CurrencyTextField";
import { formatCurrency } from "../../../../utils/namesFormater";

/** employment - a salaried workplace (LastEmploymentData); business - self employed (BizData); company - a controlling owner's company id */
export type EmploymentRecordKind = "employment" | "business" | "company";

export type EmploymentRecord = Record<string, string | number | boolean | undefined>;

interface FieldSpec {
  key: string;
  label: string;
  money?: boolean;
  digits?: boolean;
  /** the card's title */
  title?: boolean;
  /** the width in the edit grid (of 12) */
  sm?: number;
  /** always shown (empty - "-"), in one row */
  chipRow?: boolean;
}

const KIND: Record<
  EmploymentRecordKind,
  { type: PolicyEmploymentStatus; icon: React.ReactNode; untitled: string; fields: FieldSpec[]; activeSwitch?: boolean }
> = {
  employment: {
    type: PolicyEmploymentStatus.EMPLOYEE,
    icon: <WorkIcon />,
    untitled: "מקום עבודה",
    activeSwitch: true,
    fields: [
      { key: "employer_name", label: "שם מעסיק", title: true, sm: 6 },
      { key: "employer_additional_name", label: "שם נוסף למעסיק", sm: 6 },
      { key: "occupation", label: "עיסוק / תפקיד", sm: 12 },
      { key: "gross_salary", label: "שכר ברוטו", money: true, sm: 6, chipRow: true },
      { key: "net_salary", label: "שכר נטו", money: true, sm: 6, chipRow: true },
    ],
  },
  business: {
    type: PolicyEmploymentStatus.SELF_EMPLOYED,
    icon: <StorefrontIcon />,
    untitled: "עסק",
    fields: [
      { key: "business_name", label: "שם העסק", title: true, sm: 6 },
      { key: "biz_id", label: "ח.פ / ע.מ", digits: true, sm: 6, chipRow: true },
      { key: "business_sector", label: "תחום העיסוק", sm: 6, chipRow: true },
      { key: "annual_revenue", label: "מחזור הכנסות שנתי", money: true, sm: 6, chipRow: true },
      { key: "business_address", label: "כתובת העסק", sm: 12 },
    ],
  },
  company: {
    type: PolicyEmploymentStatus.CONTROLLING_OWNER,
    icon: <BusinessIcon />,
    untitled: "חברה",
    fields: [{ key: "primary_biz_id", label: "ח.פ חברה ראשית", title: true, digits: true, sm: 12 }],
  },
};

const isEmpty = (v: unknown) => v === undefined || v === null || v === "";

/** only what is filled in */
const clean = (record: EmploymentRecord): EmploymentRecord =>
  Object.fromEntries(
    Object.entries(record)
      .map(([k, v]) => [k, typeof v === "string" ? v.trim() : v])
      .filter(([, v]) => !isEmpty(v))
  );

interface EmploymentRecordCardProps {
  kind: EmploymentRecordKind;
  /** null - a new record, opened in edit mode */
  value: EmploymentRecord | null;
  /** a new record's prefilled fields (e.g. an employer found in the funds) */
  initialDraft?: EmploymentRecord;
  onSave: (record: EmploymentRecord) => Promise<void>;
  onDelete?: () => Promise<void>;
  /** a new record - cancelled */
  onCancelNew?: () => void;
}

/** a workplace / business / company - the filled in details, its type chip in the corner; ערוך opens it for editing */
const EmploymentRecordCard: React.FC<EmploymentRecordCardProps> = ({ kind, value, initialDraft, onSave, onDelete, onCancelNew }) => {
  const spec = KIND[kind];
  // a new workplace is active by default
  const [draft, setDraft] = useState<EmploymentRecord | null>(
    value ? null : { ...(spec.activeSwitch ? { is_active: true } : {}), ...initialDraft }
  );
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const titleField = spec.fields.find((f) => f.title)!;
  const shown = value ?? {};
  const title = shown[titleField.key];
  const details = spec.fields.filter((f) => !f.title && !f.chipRow && !isEmpty(shown[f.key]));
  const chipRow = spec.fields.filter((f) => f.chipRow);
  const typeChip = <EmploymentTypeChip employmentType={spec.type} variant="filled" size="small" />;

  const close = () => {
    setDraft(null);
    setConfirmDelete(false);
    if (!value) onCancelNew?.();
  };

  const run = async (action: () => Promise<void>) => {
    setSaving(true);
    try {
      await action();
      setDraft(null);
      setConfirmDelete(false);
    } finally {
      setSaving(false);
    }
  };

  const display = (f: FieldSpec, v: unknown) => (f.money ? formatCurrency(Number(v)) : String(v));

  return (
    <Box sx={{ width: "100%", p: 2, borderRadius: 2, boxShadow: 1, backgroundColor: "background.default" }}>
      {/* header - icon, title; the type chip and ערוך at the left */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "action.hover",
            color: "text.secondary",
            flexShrink: 0,
          }}
        >
          {spec.icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* the title, the type chip right after it */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            <Typography variant="h6" fontWeight={500} noWrap>
              {draft ? (value ? "עריכה" : `${spec.untitled} חדש`) : !isEmpty(title) ? (kind === "company" ? `ח.פ ${title}` : String(title)) : spec.untitled}
            </Typography>
            {typeChip}
          </Box>
          {!draft && spec.activeSwitch && shown.is_active !== undefined && (
            <Typography variant="body2" color={shown.is_active ? "success.main" : "text.secondary"}>
              {shown.is_active ? "העסקה פעילה" : "העסקה הסתיימה"}
            </Typography>
          )}
        </Box>
        {!draft && (
          <Button size="small" onClick={() => setDraft({ ...shown })}>
            ערוך
          </Button>
        )}
      </Box>

      {/* the main row (the salaries / the business id, sector and revenue) - always shown (empty - "-") */}
      {!draft && chipRow.length > 0 && (
        <Box sx={{ display: "flex", alignItems: "flex-end", flexWrap: "wrap", columnGap: 4, rowGap: 1, mt: 1, pr: 6.5 }}>
          {chipRow.map((f) => (
            <Box key={f.key}>
              <Typography variant="caption" color="text.secondary" display="block">
                {f.label}
              </Typography>
              <Typography fontWeight={500}>{isEmpty(shown[f.key]) ? "-" : display(f, shown[f.key])}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* view - the other details (e.g. the business address), only when filled in, below the main row */}
      {!draft && details.length > 0 && (
        <Grid container spacing={1.5} sx={{ mt: 0.5, pr: 6.5 }}>
          {details.map((f) => (
            <Grid item xs={12} sm={6} key={f.key}>
              <Typography variant="caption" color="text.secondary" display="block">
                {f.label}
              </Typography>
              <Typography fontWeight={500}>{display(f, shown[f.key])}</Typography>
            </Grid>
          ))}
        </Grid>
      )}

      {/* edit */}
      {draft && (
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={1.5}>
            {spec.fields.map((f, i) => (
              <Grid item xs={12} sm={f.sm ?? 6} key={f.key}>
                {f.money ? (
                  <CurrencyTextField
                    size="small"
                    fullWidth
                    maxWidth="100%"
                    label={f.label}
                    value={isEmpty(draft[f.key]) ? null : Number(draft[f.key])}
                    onChange={(v) => setDraft({ ...draft, [f.key]: v ?? undefined })}
                  />
                ) : (
                  <TextField
                    size="small"
                    fullWidth
                    label={f.label}
                    value={draft[f.key] ?? ""}
                    onChange={(e) => setDraft({ ...draft, [f.key]: f.digits ? e.target.value.replace(/\D/g, "") : e.target.value })}
                    inputProps={f.digits ? { inputMode: "numeric", dir: "ltr" } : undefined}
                    autoFocus={i === 0}
                  />
                )}
              </Grid>
            ))}
          </Grid>
          {spec.activeSwitch && (
            <FormControlLabel
              sx={{ mt: 1 }}
              control={<Switch checked={draft.is_active === true} onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })} />}
              label="העסקה פעילה"
            />
          )}
          <Box sx={{ display: "flex", gap: 1, mt: 1.5, alignItems: "center" }}>
            <Button
              variant="contained"
              onClick={() => run(() => onSave(clean(draft)))}
              disabled={saving || Object.keys(clean(draft)).length === 0}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {value ? "שמור" : "הוסף"}
            </Button>
            <Button variant="outlined" onClick={close} disabled={saving}>
              חזור
            </Button>
            <Box sx={{ flex: 1 }} />
            {value &&
              onDelete &&
              (confirmDelete ? (
                <Button color="error" variant="contained" onClick={() => run(onDelete)} disabled={saving}>
                  בטוח? מחק
                </Button>
              ) : (
                <Button color="error" startIcon={<DeleteOutlineIcon sx={{ ml: 1 }} />} onClick={() => setConfirmDelete(true)} disabled={saving}>
                  מחק
                </Button>
              ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default EmploymentRecordCard;
